// Globale Weltzeit (Konzept §5). Eine Uhr für alle, objektiv, weil ein
// entdeckbarer Fahrplan objektive Zeit voraussetzt.
//
// Der Server ist die Autorität. Der Client synchronisiert seinen Offset per
// Ping/Pong und rechnet dann lokal weiter, damit die Uhr nicht ruckelt.

import {
  TIME_SCALE, SEASON_EPOCH_MS, GAME_SECONDS_PER_DAY, REAL_SECONDS_PER_GAME_DAY,
} from './constants.js';

/** Weltsekunden seit Season-Epoche, aus einer Echtzeit-ms-Zahl. */
export function worldSecondsFromRealMs(realMs) {
  return ((realMs - SEASON_EPOCH_MS) / 1000) * TIME_SCALE;
}

/** Echtzeit-ms aus Weltsekunden (Umkehrung). */
export function realMsFromWorldSeconds(worldSeconds) {
  return SEASON_EPOCH_MS + (worldSeconds / TIME_SCALE) * 1000;
}

/** Aktuelle Weltzeit in Sekunden (Server: direkt; Client: mit Offset). */
export function nowWorldSeconds(clockOffsetMs = 0) {
  return worldSecondsFromRealMs(Date.now() + clockOffsetMs);
}

/** Zerlegt Weltsekunden in Tag / Stunde / Minute / Sekunde. */
export function breakdown(worldSeconds) {
  const day = Math.floor(worldSeconds / GAME_SECONDS_PER_DAY);
  const inDay = worldSeconds - day * GAME_SECONDS_PER_DAY;
  const hour = Math.floor(inDay / 3600);
  const minute = Math.floor((inDay % 3600) / 60);
  const second = Math.floor(inDay % 60);
  return { day, inDay, hour, minute, second };
}

/** Sekunden seit Mitternacht der aktuellen Weltzeit. */
export function secondsIntoDay(worldSeconds) {
  const r = worldSeconds % GAME_SECONDS_PER_DAY;
  return r < 0 ? r + GAME_SECONDS_PER_DAY : r;
}

/** Tagesindex seit Season-Start. */
export function dayIndex(worldSeconds) {
  return Math.floor(worldSeconds / GAME_SECONDS_PER_DAY);
}

/** "Tag 3 04:17" — bewusst ohne Sekunden, das Spiel ist keine Stoppuhr. */
export function formatClock(worldSeconds, withSeconds = false) {
  const b = breakdown(worldSeconds);
  const hh = String(b.hour).padStart(2, '0');
  const mm = String(b.minute).padStart(2, '0');
  if (!withSeconds) return `Tag ${b.day} ${hh}:${mm}`;
  const ss = String(b.second).padStart(2, '0');
  return `Tag ${b.day} ${hh}:${mm}:${ss}`;
}

/** Nur die Uhrzeit, für kompakte Anzeigen. */
export function formatTimeOfDay(worldSeconds) {
  const b = breakdown(worldSeconds);
  return `${String(b.hour).padStart(2, '0')}:${String(b.minute).padStart(2, '0')}`;
}

/** Dauer in Weltsekunden menschenlesbar ("2 h 14 min", "45 min", "20 s"). */
export function formatDuration(worldSeconds) {
  const s = Math.max(0, Math.round(worldSeconds));
  if (s < 90) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, '0')} min`;
}

/**
 * Sonnenstand als Phase 0..1 (0 = Mitternacht, 0.5 = Mittag).
 * Wird von Himmel, Licht und Sichtweite benutzt.
 */
export function sunPhase(worldSeconds) {
  return secondsIntoDay(worldSeconds) / GAME_SECONDS_PER_DAY;
}

/** Sonnenhöhe -1..1 (grob, ohne Jahreszeit/Breitengrad). */
export function sunElevation(worldSeconds) {
  // Sonnenaufgang ~06:00, Untergang ~20:00: leicht verlängerter Tag.
  const t = sunPhase(worldSeconds);
  return Math.sin((t - 0.25) * Math.PI * 2) * 0.92 + 0.06;
}

export function isNight(worldSeconds) {
  return sunElevation(worldSeconds) < -0.06;
}

/** Echtzeitsekunden, die eine Weltsekunden-Dauer braucht. */
export function toRealSeconds(worldSeconds) {
  return worldSeconds / TIME_SCALE;
}

/** Weltsekunden, die in einer Echtzeitdauer vergehen. */
export function toWorldSeconds(realSeconds) {
  return realSeconds * TIME_SCALE;
}

export const TIME_INFO = Object.freeze({
  scale: TIME_SCALE,
  realSecondsPerGameDay: REAL_SECONDS_PER_GAME_DAY,
  gameSecondsPerDay: GAME_SECONDS_PER_DAY,
  epochMs: SEASON_EPOCH_MS,
});
